"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  Clock,
  MapPin,
  Star,
  Share2,
  Users,
  Calendar,
  Globe,
  Car,
  Shield,
  Search,
} from "lucide-react";
import {
  getExperiences,
  getExperienceById,
} from "@/services/experience.service";
import type { ExperienceListItem, ExperienceDetail } from "@/types/experience";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

// const CATEGORY_IMAGES: Record<string, string> = {
//   "White Water Rafting": `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/1.png`,
//   "Hiking and Trekking": `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/2.png`,
//   Surfing: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/3.png`,
//   "Wildlife Safari": `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/4.png`,
//   "Zip Lining": `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/5.png`,
//   "Rock Climbing": `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/6.png`,
// };

// Fallback image shown only when an experience has no coverImageUrl.
// Keyed by the API's uppercase category value.
const CATEGORY_IMAGES: Record<string, string> = {
  ADVENTURE: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/1.png`,
  WELLNESS: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/2.png`,
  CULINARY: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/3.png`,
  CULTURE: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/4.png`,
  FESTIVAL: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/explore/adventure-outdoors/5.png`,
};

function getImageForCategory(category: string): string | null {
  return CATEGORY_IMAGES[category.toUpperCase()] ?? null;
}

const CATEGORIES = ["Adventure", "Wellness", "Culinary", "Culture", "Festival"];

// API returns category as an uppercase enum-style string (e.g. "ADVENTURE"),
// pills display it Capitalized (e.g. "Adventure") — compare case-insensitively.
function categoryMatches(expCategory: string, selected: string): boolean {
  return expCategory.trim().toUpperCase() === selected.trim().toUpperCase();
}

function parseDifficulty(
  detail: ExperienceDetail | undefined,
): "Easy" | "Moderate" | "Challenging" {
  if (!detail?.categoryFieldsJson) return "Moderate";
  try {
    const fields = JSON.parse(detail.categoryFieldsJson) as Record<
      string,
      unknown
    >;
    const raw = (fields.difficulty ?? fields.Difficulty ?? "") as string;
    if (["Easy", "Moderate", "Challenging"].includes(raw)) {
      return raw as "Easy" | "Moderate" | "Challenging";
    }
  } catch {}
  return "Moderate";
}

function difficultyColor(d: string) {
  switch (d) {
    case "Easy":
      return "bg-green-500";
    case "Challenging":
      return "bg-red-500";
    default:
      return "bg-orange-500";
  }
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  de: "German",
  ta: "Tamil",
  si: "Sinhala",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  ru: "Russian",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ar: "Arabic",
  hi: "Hindi",
  nl: "Dutch",
  pt: "Portuguese",
};

function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code.toLowerCase()] ?? code.toUpperCase();
}

function formatPrice(price: number): string {
  return `LKR ${price.toLocaleString()}`;
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "…";
}

function ExperienceCard({
  exp,
  detail,
}: {
  exp: ExperienceListItem;
  detail: ExperienceDetail | undefined;
}) {
  const router = useRouter();
  const [imgError, setImgError] = useState(false);
  const [shared, setShared] = useState(false);
  const difficulty = parseDifficulty(detail);

  const imgSrc = imgError
    ? null
    : (exp.coverImageUrl ?? getImageForCategory(exp.category));

  const handleCheckAvailability = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(`/activities/${exp.id}/availability`);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/activities/${exp.id}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: exp.title,
          text: `Check out ${exp.title} on LilyLanka`,
          url: shareUrl,
        });
      } catch {
        // user cancelled share sheet — no-op
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch {
        // clipboard write failed — no-op
      }
    }
  };

  return (
    <Link href={`/activities/${exp.id}`} className="block h-full">
      <div className="group relative aspect-[7/10] rounded-3xl overflow-hidden bg-stone-200 shadow-lg shadow-stone-300/50 hover:shadow-2xl hover:shadow-stone-400/40 transition-shadow duration-300">
        {/* Photo */}
        {imgSrc ? (
          <Image
            src={imgSrc}
            alt={exp.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="absolute inset-0 bg-stone-300 flex items-center justify-center">
            <span className="text-stone-500 text-xs font-medium px-4 text-center">
              {exp.category}
            </span>
          </div>
        )}

        {/* Gradient scrim for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/5" />

        {/* Share */}
        <button
          onClick={handleShare}
          aria-label="Share experience"
          className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 z-20 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
        </button>
        {shared && (
          <span className="absolute top-2.5 right-11 sm:top-4 sm:right-14 z-20 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-black/70 text-white text-[9px] sm:text-[11px] font-semibold whitespace-nowrap">
            Link copied!
          </span>
        )}

        {/* Content overlay */}
        <div className="absolute inset-x-0 bottom-0 z-10 p-3 pb-14 sm:p-5 sm:pb-20 flex flex-col gap-1 sm:gap-1.5">
          <div className="flex flex-col gap-2">
            <h3 className="text-white font-bold text-sm sm:text-base leading-snug overflow-hidden max-h-10">
              {exp.title}
            </h3>
            <span className="text-purple-400 font-bold text-xs sm:text-sm">
              {formatPrice(exp.pricePerPersonLkr)}
            </span>
          </div>
          <p className="hidden sm:block text-white/80 text-xs leading-relaxed overflow-hidden max-h-8">
            {truncateText(
              detail?.description ??
                `${detail?.durationHours ?? "A few"} hours of ${exp.category.toLowerCase()} in ${exp.district}.`,
              90,
            )}
          </p>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
            <span className="flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-[10px] sm:text-xs font-semibold">
              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-amber-400 text-amber-400" />
              {difficulty}
            </span>
            <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-[10px] sm:text-xs font-semibold">
              {exp.category}
            </span>
            {detail && (
              <span className="flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-[10px] sm:text-xs font-semibold">
                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                {detail.durationHours}h
              </span>
            )}
          </div>
        </div>

        {/* Book now */}
        <button
          onClick={handleCheckAvailability}
          className="absolute bottom-2.5 left-2.5 right-2.5 sm:bottom-4 sm:left-4 sm:right-4 z-20 py-2 sm:py-3 rounded-full bg-white/95 backdrop-blur-sm text-stone-900 text-xs sm:text-sm font-bold hover:bg-white active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-1.5 sm:gap-2"
        >
          <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Check availability
        </button>
      </div>
    </Link>
  );
}
export default function AdventuresAndOutdoorsPage() {
  const [experiences, setExperiences] = useState<ExperienceListItem[]>([]);
  const [details, setDetails] = useState<Record<string, ExperienceDetail>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = 100;
      let page = 1;
      let all: ExperienceListItem[] = [];

      // loop until a page comes back short (= last page), capped as a safety net
      while (page <= 20) {
        const data = await getExperiences({ page, pageSize });
        all = all.concat(data);
        if (data.length < pageSize) break;
        page += 1;
      }

      setExperiences(all);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    experiences.forEach((exp) => {
      if (details[exp.id]) return;
      getExperienceById(exp.id)
        .then((detail) =>
          setDetails((prev) => ({ ...prev, [detail.id]: detail })),
        )
        .catch(() => {});
    });
  }, [experiences]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetched once (all pages, unfiltered); filtered here client-side so
  // switching pills or typing in search doesn't require a refetch.
  const filteredExperiences = experiences
    .filter((exp) =>
      selectedCategory === null
        ? true
        : categoryMatches(exp.category, selectedCategory),
    )
    .filter((exp) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.trim().toLowerCase();
      return (
        exp.title.toLowerCase().includes(q) ||
        exp.category.toLowerCase().includes(q) ||
        exp.district.toLowerCase().includes(q)
      );
    });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero — no search bar */}
      <div
        className="relative h-96 bg-cover bg-center pt-16 md:pt-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('${DIGITAL_OCEAN_SPACES_CDN}/Site/images/slider/slider-04.jpg')`,
        }}
      >
        <div className="flex flex-col justify-center items-center h-full text-white px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center">
            Adventures &amp; Outdoors
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-center max-w-2xl mb-8 px-2">
            Discover thrilling experiences across Sri Lanka — from white water
            rafting to mountain treks, surfing to wildlife safaris.
          </p>

          {/* Glass search bar */}
          <div className="w-full max-w-xl">
            <div className="flex items-center gap-3 px-5 py-3.5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 shadow-lg shadow-black/10 focus-within:bg-white/20 focus-within:border-white/50 transition-colors">
              <Search className="w-5 h-5 text-white/80 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search experiences, categories, or places…"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="experience-search"
                className="w-full bg-transparent text-white placeholder:text-white/70 text-sm font-medium border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus:border-0 focus-visible:outline-none appearance-none shadow-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  className="flex-shrink-0 text-white/70 hover:text-white transition-colors text-sm font-semibold"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category pills — always all shown, horizontally scrollable */}
      <div className="px-4 py-6 max-w-7xl mx-auto">
        <div className="flex gap-3 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition ${
              selectedCategory === null
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() =>
                setSelectedCategory(selectedCategory === cat ? null : cat)
              }
              className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition ${
                selectedCategory === cat
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Activities Section */}
      <div className="px-3 sm:px-4 py-8 max-w-7xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">
          {loading
            ? "Loading…"
            : `Showing ${filteredExperiences.length} result${filteredExperiences.length !== 1 ? "s" : ""}`}
        </h2>
        {error && (
          <div className="text-center py-16 text-red-600">
            <p className="text-lg font-semibold mb-2">Something went wrong</p>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <button
              onClick={fetchAll}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Retry
            </button>
          </div>
        )}

        {loading && !error && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[7/10] rounded-3xl bg-stone-200 animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && !error && filteredExperiences.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg">No experiences found.</p>
            <p className="text-sm mt-1">Try adjusting your filters.</p>
          </div>
        )}

        {!loading && !error && filteredExperiences.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {filteredExperiences.map((exp) => (
              <ExperienceCard key={exp.id} exp={exp} detail={details[exp.id]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
